import React, { useState } from 'react';
import SPAButton from '../shared/SPAButton';
import type { Finding } from './Findings';
import type { AssignmentSummary } from './useAssignmentSyncState';
import SPASectionHeading from '../shared/SPASectionHeading';

interface MessageContext {
  assignment: AssignmentSummary;
  neverLaunched: number;
}

// AIDEV-NOTE: What support sends a teacher when only the teacher can fix it — in the teacher's words,
// with no dashboard jargon. Keyed by finding code; codes with no entry are ours to fix, not theirs.
const MESSAGES: Record<string, (ctx: MessageContext) => string> = {
  classes_without_a_course: ({ assignment }) => (
    `"${assignment.title}" is assigned to ${assignment.classroom_count} of your classes in TCI, but it has `
    + `only been added in ${assignment.courses.length} of your LMS courses, so grades for the other classes `
    + 'have nowhere to go. Please copy the assignment from a course it is already in into each of the others '
    + '(in Canvas, "Copy To…"), then open it once from each. Adding it again from TCI makes a separate copy '
    + 'that will not sync. If some of those classes do not use it, let us know and we will remove them in TCI.'
  ),
  not_in_lms: ({ assignment }) => (
    `"${assignment.title}" was created in TCI, so it is not in your LMS and its grades stay in TCI. To send `
    + 'grades to your LMS gradebook, add the lesson from inside your LMS course; that creates a linked copy.'
  ),
  only_studentless_courses: ({ assignment }) => (
    `"${assignment.title}" is linked only to an LMS course with no students in it — usually a Blueprint or `
    + 'template course. Grades sent there are thrown away. Please add the assignment in the course sections '
    + 'your students are enrolled in, then open it once from each.'
  ),
  unlinked: ({ assignment }) => (
    `"${assignment.title}" has not been opened from your LMS yet, so it is not connected to a gradebook `
    + 'column. Please open the assignment once from the LMS (as the teacher), and grades will start syncing.'
  ),
  unpublished: ({ assignment }) => (
    `"${assignment.title}" is unpublished in your LMS, which rejects every grade until it is published. `
    + 'Please publish it; grades will sync on the next update.'
  ),
};

const neverLaunchedMessage = ({ assignment, neverLaunched }: MessageContext) => (
  `${neverLaunched} ${neverLaunched === 1 ? 'student has' : 'students have'} never opened a TCI assignment `
  + `from the LMS, so their grades for "${assignment.title}" cannot be matched to their LMS accounts. `
  + 'Each of them needs to open any TCI assignment from the LMS once; after that their grades will sync.'
);

/** The first finding the teacher owns, as a message support can copy — plus the never-launched note. */
export const messagesFor = (findings: Finding[], ctx: MessageContext) => {
  const own = findings.map(finding => MESSAGES[finding.code]?.(ctx)).filter((text): text is string => !!text);

  return [...own.slice(0, 1), ...(ctx.neverLaunched > 0 ? [neverLaunchedMessage(ctx)] : [])];
};

const Copyable = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);

  const copy = () => navigator.clipboard?.writeText(text).then(() => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  });

  return (
    <div className="tw-mt-2 tw-rounded tw-border tw-border-solid tw-border-spa-border tw-bg-spa-bg tw-px-3 tw-py-2">
      <p className="tw-m-0 tw-text-sm tw-text-spa-primary">{text}</p>
      <SPAButton className="tw-mt-2" size="xs" type="button" variant="secondary" onClick={copy}>
        {copied ? 'Copied' : 'Copy message'}
      </SPAButton>
    </div>
  );
};

const TeacherMessage = ({ messages }: { messages: string[] }) => {
  if (messages.length === 0) return null;

  return (
    <div className="tw-mt-3">
      <SPASectionHeading className="tw-mb-1" title="For the teacher" />
      {messages.map(text => <Copyable key={text} text={text} />)}
    </div>
  );
};

export default TeacherMessage;
